import { useEffect, useState } from 'react'
import { LOCAL_KEYS, PERSISTENT_PREFERENCE_KEYS, SESSION_KEYS } from '../constants/storageKeys'
import { STEP } from '../constants/steps'
import { safeLocalStorage, safeSessionStorage } from '../utils/safeStorage'

export const INITIAL_DATA = {
	sectionA: {
		fullName: '',
		occupation: '',
		birthday: '',
		maritalStatus: '',
		address: '',
		phone: '',
		legalRepresentative: '',
	},
	sectionB: {
		hasInsurance: null,
		insuranceDetails: '',
		hasPotentialInsurance: null,
		potentialInsuranceDetails: '',
	},
	sectionC: {
		hasMaintenanceClaims: null,
		maintenancePersonDetails: '',
	},
	sectionD: {
		hasDependents: null,
		dependents: [],
	},
	sectionE: {
		receivesSocialAssistanceSGBXII: null,
		hasPartner: null,

		self: {
			nichtselbstaendig: { has: null, brutto: '' },
			selbstaendig: { has: null, brutto: '' },
			vermietung: { has: null, brutto: '' },
			kapital: { has: null, brutto: '' },
			kindergeld: { has: null, brutto: '' },
			wohngeld: { has: null, brutto: '' },
			unterhalt: { has: null, brutto: '' },
			rente: { has: null, brutto: '' },
			arbeitslosengeld: { has: null, brutto: '' },
			buergergeld: { has: null, brutto: '' },
			krankengeld: { has: null, brutto: '' },
			elterngeld: { has: null, brutto: '' },
			sonstige: {
				has: null,
				items: [{ details: '', brutto: '' }],
			},
			noIncomeExplanation: '',
		},
		partner: {
			nichtselbstaendig: { has: null, brutto: '' },
			selbstaendig: { has: null, brutto: '' },
			vermietung: { has: null, brutto: '' },
			kapital: { has: null, brutto: '' },
			kindergeld: { has: null, brutto: '' },
			wohngeld: { has: null, brutto: '' },
			unterhalt: { has: null, brutto: '' },
			rente: { has: null, brutto: '' },
			arbeitslosengeld: { has: null, brutto: '' },
			buergergeld: { has: null, brutto: '' },
			krankengeld: { has: null, brutto: '' },
			elterngeld: { has: null, brutto: '' },
			sonstige: {
				has: null,
				items: [{ details: '', brutto: '' }],
			},
			noIncomeExplanation: '',
		},
	},
	sectionF: {
		hasDeductions: null,
	},
	sectionG: {
		bankAccounts: { has: null, description: '', value: '' },
		realEstate: { has: null, description: '', value: '' },
		vehicles: { has: null, description: '', value: '' },
		cash: { has: null, description: '', value: '' },
		lifeInsurance: { has: null, description: '', value: '' },
		otherAssets: { has: null, description: '', value: '' },
	},
	sectionH: {
		livingSpace: '',
		numberOfRooms: '',
		totalPeople: '',
		housingType: '',
		rentCold: '',
		heatingCosts: '',
		otherCosts: '',
		totalRent: '',
		ownShareRent: '',
		interestRepayment: '',
		heatingCostsOwner: '',
		otherCostsOwner: '',
		totalCostOwner: '',
		ownShareOwner: '',
		loanDetails: '',
		loans: [{ remainingDebt: '', monthlyPayment: '' }],
	},
	sectionI: {
		hasObligations: null,
		obligations: [{ description: '', remainingDebt: '', monthlyPayment: '', ownShare: '' }],
	},
	sectionJ: {
		hasSpecialLoads: null,
		loads: [{ description: '', amount: '' }],
	},
	sectionK: {
		signature: null,
		confirmed: false,
		date: new Date().toLocaleDateString('de-DE', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		}),
	},
}

// Uploaded files and the signature live in App state only (never persisted),
// so a reload cannot restore them. Resuming past the upload step would let the
// user reach the PDF with the attachments silently gone; send them back to the
// upload step instead.
// Arrays whose INITIAL_DATA default is empty carry no element shape to check
// against, so the shape of one element is declared here.
const ARRAY_ITEM_TEMPLATES = {
	dependents: {
		id: 0,
		name: '',
		birthday: '',
		relationship: '',
		monthlyAmount: '',
		hasOwnIncome: '',
		incomeAmount: '',
	},
}

// Elements that are not objects (tampered or stale storage) are dropped;
// the rest are checked field by field like any other section.
const sanitizeArray = (key, value, templateValue) => {
	if (!Array.isArray(value)) return templateValue
	const itemTemplate = templateValue[0] ?? ARRAY_ITEM_TEMPLATES[key]
	if (!itemTemplate || typeof itemTemplate !== 'object') return value

	const items = value
		.filter(item => typeof item === 'object' && item !== null && !Array.isArray(item))
		.map((item, index) => {
			const clean = sanitizeFormData(item, itemTemplate)
			// Dependent cards use id as their React key; keep it unique.
			if ('id' in itemTemplate && !(typeof clean.id === 'number' && clean.id > 0)) {
				clean.id = Date.now() + index
			}
			return clean
		})
	// Sections with a non-empty default expect at least one row to render.
	return items.length === 0 && templateValue.length > 0 ? templateValue : items
}

const sanitizeFormData = (parsed, template) => {
	if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
		return template
	}
	const clean = {}
	for (const key of Object.keys(template)) {
		const templateValue = template[key]
		const value = parsed[key]
		if (Array.isArray(templateValue)) {
			clean[key] = sanitizeArray(key, value, templateValue)
		} else if (templateValue !== null && typeof templateValue === 'object') {
			clean[key] = sanitizeFormData(value, templateValue)
		} else if (templateValue === null) {
			const type = typeof value
			clean[key] =
				value === null || type === 'string' || type === 'boolean' || type === 'number'
					? value
					: templateValue
		} else {
			clean[key] = value === null || typeof value === typeof templateValue ? value : templateValue
		}
	}
	return clean
}

export const useFormData = () => {
	const [step, setStep] = useState(() => {
		const hasConsented = safeLocalStorage.getItem(LOCAL_KEYS.CONSENT) === 'true'
		if (!hasConsented) return 0

		const savedStep =
			safeSessionStorage.getItem(SESSION_KEYS.STEP) ?? safeLocalStorage.getItem(SESSION_KEYS.STEP)
		const parsedStep = parseInt(savedStep, 10)
		if (!Number.isInteger(parsedStep) || parsedStep < 0 || parsedStep > STEP.K) return 0
		return Math.min(parsedStep, STEP.L)
	})

	const [formData, setFormData] = useState(() => {
		const savedData =
			safeSessionStorage.getItem(SESSION_KEYS.FORM_DATA) ??
			safeLocalStorage.getItem(SESSION_KEYS.FORM_DATA)
		if (!savedData) return INITIAL_DATA

		try {
			const parsed = JSON.parse(savedData)
			return sanitizeFormData(parsed, INITIAL_DATA)
		} catch (e) {
			console.error('Data parsing error, resetting form', e)
			return INITIAL_DATA
		}
	})

	useEffect(() => {
		safeSessionStorage.setItem(SESSION_KEYS.FORM_DATA, JSON.stringify(formData))
		safeLocalStorage.removeItem(SESSION_KEYS.FORM_DATA)
	}, [formData])

	useEffect(() => {
		safeSessionStorage.setItem(SESSION_KEYS.STEP, step.toString())
		safeLocalStorage.removeItem(SESSION_KEYS.STEP)
	}, [step])

	const updateData = (section, field, value) => {
		setFormData(prev => ({
			...prev,
			[section]: { ...prev[section], [field]: value },
		}))
	}

	const mergeSectionData = (section, partialData) => {
		setFormData(prev => ({
			...prev,
			[section]: { ...prev[section], ...partialData },
		}))
	}

	// keepStep: wipe the data but stay on the current screen (used right after
	// the PDF was downloaded, when the finish screen is still showing).
	const resetFormData = ({ keepStep = false } = {}) => {
		// Interface preferences stay; everything else, the consent flag included,
		// goes, so the next application starts with the consent screen again.
		const preferences = PERSISTENT_PREFERENCE_KEYS.map(key => [key, safeLocalStorage.getItem(key)])
		safeLocalStorage.clear()
		safeSessionStorage.clear()
		preferences.forEach(([key, value]) => {
			if (value !== null) safeLocalStorage.setItem(key, value)
		})

		setFormData(INITIAL_DATA)
		if (!keepStep) setStep(0)
	}

	return { step, setStep, formData, updateData, mergeSectionData, resetFormData }
}
